/**
 * Makanly Design System v3.0 - Card Component
 *
 * Card Styles:
 * - Standard: White bg, subtle shadow (default)
 * - Dark: Gray-900 bg for analytics/dashboards
 * - Accent: Teal/Gold bg for info highlights
 * - Premium: Gold gradient for loyalty/rewards
 * - Glass: Glassmorphism effect
 * - iOS: Larger rounded corners (24px) for mobile
 *
 * @see /docs/design/UI-DESIGN-CONTRACT.md
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const cardVariants = cva('rounded-lg text-card-foreground', {
  variants: {
    variant: {
      // Default - White elevated surface
      default: 'bg-card border border-border shadow-sm',

      // Dark - For analytics/dashboard cards
      dark: 'bg-gray-900 text-white border border-gray-800',

      // Teal - Primary brand highlight
      teal: 'bg-primary-50 text-primary-900 border border-primary-200',

      // Accent - Gold highlight for rewards/premium
      accent: 'bg-accent-50 text-accent-900 border border-accent-200',

      // Premium - Gold gradient for loyalty features
      premium:
        'bg-gradient-to-br from-accent-400 via-accent-500 to-accent-600 text-gray-900 border-none shadow-gold-glow',

      // Glass - Glassmorphism effect
      glass:
        'bg-white/75 backdrop-blur-lg border border-white/50 dark:bg-gray-900/75 dark:border-gray-700/50',

      // Outline - No background, just border
      outline: 'bg-transparent border border-border',

      // Ghost - No border or shadow
      ghost: 'bg-transparent border-none shadow-none',

      // Interactive - Hover effect for clickable cards
      interactive:
        'bg-card border border-border shadow-sm hover:shadow-card-hover hover:border-primary/20 transition-all duration-200 cursor-pointer',
    },
    size: {
      default: 'p-0',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    },
    rounded: {
      default: 'rounded-lg', // 12px
      sm: 'rounded-md', // 8px
      lg: 'rounded-xl', // 16px
      xl: 'rounded-2xl', // 24px - iOS style
      full: 'rounded-3xl', // 32px
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    rounded: 'default',
  },
});

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, size, rounded, ...props }, ref) => (
    <div ref={ref} className={cn(cardVariants({ variant, size, rounded, className }))} {...props} />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn('text-2xl font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

/**
 * Stat Card - Specialized card for displaying metrics (NEW in v3.0)
 */
export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'dark' | 'teal' | 'accent';
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, title, value, description, icon, trend, variant = 'default', ...props }, ref) => {
    const variantClasses = {
      default: 'bg-card border border-border',
      dark: 'bg-gray-900 text-white border border-gray-800',
      teal: 'bg-primary-50 text-primary-900 border border-primary-200',
      accent: 'bg-accent-50 text-accent-900 border border-accent-200',
    };

    return (
      <div
        ref={ref}
        className={cn('rounded-lg p-6 shadow-sm', variantClasses[variant], className)}
        {...props}
      >
        <div className="flex items-center justify-between">
          <p
            className={cn(
              'text-sm font-medium',
              variant === 'dark' ? 'text-gray-400' : 'text-muted-foreground',
            )}
          >
            {title}
          </p>
          {icon && (
            <div
              className={cn(
                'h-8 w-8 rounded-full flex items-center justify-center',
                variant === 'dark' ? 'bg-gray-800' : 'bg-gray-100',
              )}
            >
              {icon}
            </div>
          )}
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <p
            className={cn(
              'text-3xl font-bold tracking-tight',
              variant === 'dark' ? 'text-white' : 'text-foreground',
            )}
          >
            {value}
          </p>
          {trend && (
            <span
              className={cn(
                'text-sm font-medium',
                trend.isPositive ? 'text-emerald-500' : 'text-red-500',
              )}
            >
              {trend.isPositive ? '+' : ''}
              {trend.value}%
            </span>
          )}
        </div>
        {description && (
          <p
            className={cn(
              'mt-1 text-sm',
              variant === 'dark' ? 'text-gray-400' : 'text-muted-foreground',
            )}
          >
            {description}
          </p>
        )}
      </div>
    );
  },
);
StatCard.displayName = 'StatCard';

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  StatCard,
  cardVariants,
};
